import React from 'react'
import {
  Avatar,
  Button,
  Col,
  Form,
  Input,
  Row,
  Select,
  Spin,
  Typography,
} from 'antd'
import { SaveOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '~/Store'
import { UserActions } from '~/Store/User'
import { getCurrentUser, getUserLoading } from '~/Store/User/selectors'
import { ScrollBar } from '~/Components/ScrollBar'

const { Title, Link } = Typography
const { Item } = Form

export const EditProfile = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const currentUser = useAppSelector(getCurrentUser)
  const loading = useAppSelector(getUserLoading)

  const cancelEditProfile = () => {
    dispatch(UserActions.setEditState(false))
  }

  const saveChanges = (values: any) => {
    const profileData = {
      ...currentUser,
      firstname: values.firstname,
      lastname: values.lastname,
      profile: {
        ...currentUser?.profile,
        gender: values.gender,
        address: values.address,
        address2: values.address2,
        country: values.country,
        city: values.city,
        state: values.state,
        zip: values.zip,
        workAddress: values.workAddress,
        workAddress2: values.workAddress2,
        workCountry: values.workCountry,
        workCity: values.workCity,
        workState: values.workState,
        workZip: values.workZip,
      },
    }
    dispatch(UserActions.updateUserProfileInfo(profileData))
  }

  return (
    <>
      <Form
        layout="vertical"
        initialValues={{
          ...currentUser,
          ...currentUser?.profile,
        }}
        onFinish={saveChanges}
      >
        <Row align="stretch">
          <Title style={{ flex: 1 }} level={4}>
            {t('profile.personalInformation')}
          </Title>
          <>
            <Button
              danger
              style={{ marginRight: '5px' }}
              onClick={cancelEditProfile}
            >
              {t('cancel')}
            </Button>
            <Button htmlType="submit" type="primary" icon={<SaveOutlined />}>
              {t('saveChanges')}
            </Button>
          </>
        </Row>
        <Row>
          <Col md={24}>
            <ScrollBar
              style={{
                height: 'calc(100vh - 230px)',
                paddingRight: '16px',
                marginRight: '-16px',
              }}
            >
              <Spin spinning={loading}>
                <Row style={{ marginBottom: '15px' }}>
                  <Avatar shape="square" size="large" />
                  <Link style={{ marginLeft: '10px', marginTop: '10px' }}>
                    {t('changeAvatar')}
                  </Link>
                </Row>
                <Item label={t('profile.name')} noStyle>
                  <Item
                    name="firstname"
                    style={{
                      display: 'inline-block',
                      width: 'calc(50% - 8px)',
                    }}
                    rules={[
                      { required: true, message: t('profile.firstNameRequired') },
                    ]}
                  >
                    <Input placeholder={t('profile.firstNamePlaceholder')} />
                  </Item>
                  <Item
                    name="lastname"
                    style={{
                      display: 'inline-block',
                      width: 'calc(50% - 0px)',
                      margin: '0 0 0 8px',
                    }}
                    rules={[
                      { required: true, message: t('profile.lastNameRequired') },
                    ]}
                  >
                    <Input placeholder={t('profile.lastNamePlaceholder')} />
                  </Item>
                </Item>
                <Item
                  label={t('profile.gender')}
                  name="gender"
                  style={{ marginBottom: '8px' }}
                >
                  <Select>
                    <Select.Option value="Male">{t('profile.male')}</Select.Option>
                    <Select.Option value="Female">{t('profile.female')}</Select.Option>
                    <Select.Option value="">{t('profile.unknown')}</Select.Option>
                  </Select>
                </Item>
                <Item
                  label={t('profile.personalAddress')}
                  style={{ marginBottom: 0, fontWeight: 'bold' }}
                >
                  <Item name="address" style={{ marginBottom: '8px' }}>
                    <Input placeholder={t('profile.streetAddress')} />
                  </Item>
                  <Item name="address2" style={{ marginBottom: '8px' }}>
                    <Input placeholder={t('profile.streetAddress2')} />
                  </Item>
                </Item>
                <Item style={{ marginBottom: 0 }}>
                  <Item
                    label={t('profile.country')}
                    name="country"
                    style={{
                      display: 'inline-block',
                      width: 'calc(50% - 8px)',
                      marginBottom: '8px',
                    }}
                  >
                    <Input placeholder={t('profile.country')} />
                  </Item>
                  <Item
                    label={t('city')}
                    name="city"
                    style={{
                      display: 'inline-block',
                      width: 'calc(50% - 0px)',
                      margin: '0 0 0 8px',
                    }}
                  >
                    <Input placeholder={t('city')} />
                  </Item>
                </Item>
                <Item style={{ marginBottom: 0 }}>
                  <Item
                    label={t('state')}
                    name="state"
                    style={{
                      display: 'inline-block',
                      width: 'calc(50% - 8px)',
                    }}
                  >
                    <Input placeholder={t('state')} />
                  </Item>
                  <Item
                    label={t('zip')}
                    name="zip"
                    style={{
                      display: 'inline-block',
                      width: 'calc(50% - 0px)',
                      margin: '0 0 0 8px',
                    }}
                  >
                    <Input placeholder={t('zip')} />
                  </Item>
                </Item>
                <Item
                  label={t('profile.workAddress')}
                  style={{ marginBottom: 0, fontWeight: 'bold' }}
                >
                  <Item name="workAddress" style={{ marginBottom: '8px' }}>
                    <Input placeholder={t('profile.streetAddress')} />
                  </Item>
                  <Item name="workAddress2" style={{ marginBottom: '8px' }}>
                    <Input placeholder={t('profile.streetAddress2')} />
                  </Item>
                </Item>
                <Item style={{ marginBottom: 0 }}>
                  <Item
                    label={t('profile.country')}
                    name="workCountry"
                    style={{
                      display: 'inline-block',
                      width: 'calc(50% - 8px)',
                      marginBottom: '8px',
                    }}
                  >
                    <Input placeholder={t('profile.country')} />
                  </Item>
                  <Item
                    label={t('city')}
                    name="workCity"
                    style={{
                      display: 'inline-block',
                      width: 'calc(50% - 0px)',
                      margin: '0 0 0 8px',
                    }}
                  >
                    <Input placeholder={t('city')} />
                  </Item>
                </Item>
                <Item style={{ marginBottom: 0 }}>
                  <Item
                    label={t('state')}
                    name="workState"
                    style={{
                      display: 'inline-block',
                      width: 'calc(50% - 8px)',
                    }}
                  >
                    <Input placeholder={t('state')} />
                  </Item>
                  <Item
                    label={t('zip')}
                    name="workZip"
                    style={{
                      display: 'inline-block',
                      width: 'calc(50% - 0px)',
                      margin: '0 0 0 8px',
                    }}
                  >
                    <Input placeholder={t('zip')} />
                  </Item>
                </Item>
              </Spin>
            </ScrollBar>
          </Col>
        </Row>
      </Form>
    </>
  )
}